import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './assets/techwise-logo.png';
import downloadIcon from './assets/download.png';
import './theme.css';
import userIcon from './user.png';


function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadDocxFile(url: string, filename: string) {
  fetch(url, { credentials: 'include' })
    .then(res => {
      if (!res.ok) throw new Error('Failed to download file');
      return res.blob();
    })
    .then(blob => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch(err => {
      alert('Download failed: ' + err.message);
    });
}

const UploadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

interface MainAppProps {
  user: {
    user_id: string;
    email: string;
    name?: string;
  };
}

interface AgentTrace {
  step: string;
  content: string;
  timestamp: string;
}

interface Case {
  case_id: string;
  file_path: string;
  status: string;
  created_at: string;
  agent_trace: AgentTrace[];
}

interface SessionData {
  current_case_index: number;
  last_known_stage: string;
  cases: Case[];
  agent_response: string;
  status: string;
}

interface PendingHumanState {
  session_id: string;
  case_id: string;
  case_number: number;
  prompt?: string;
  context?: any;
  workflow_state?: any;
}

interface DownloadLinks {
  compactJd?: string;
  enhancementSummary?: string;
  enhancedJd?: string;
  resumeRankingCsv?: string;
  resumeRankingDocx?: string;
  questionsAnalysis?: string;


}

interface Message {
  sender: 'bot' | 'user';
  text: string;
}

const normalizeMessageText = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'text' in item && typeof item.text === 'string') {
          return item.text;
        }
        return '';
      })
      .join('');
  }
  if (value == null) return fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
};

const sanitizeIncomingMessages = (value: unknown): Message[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];

    const candidate = item as { sender?: unknown; text?: unknown };
    const sender = candidate.sender === 'user' ? 'user' : candidate.sender === 'bot' ? 'bot' : null;
    if (!sender) return [];

    return [{
      sender,
      text: normalizeMessageText(candidate.text),
    }];
  });
};

const MainApp: React.FC<MainAppProps> = ({ user }) => {
  const [jdPath, setJdPath] = useState<string>('');
  const [resumePath, setResumePath] = useState<string>('');
  const [jdLoading, setJdLoading] = useState<boolean>(false);
  const [resumeLoading, setResumeLoading] = useState<boolean>(false);
  const [hoverJD, setHoverJD] = useState<boolean>(false);
  const [hoverResume, setHoverResume] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [jdFiles, setJdFiles] = useState<File[]>([]);
  const [pendingHumanPrompt, setPendingHumanPrompt] = useState<string | null>(null);
  const [pendingHumanState, setPendingHumanState] = useState<PendingHumanState | null>(null);
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const [jdProcessingActive, setJdProcessingActive] = useState<boolean>(false);
  const [downloadLinks, setDownloadLinks] = useState<DownloadLinks>({});
  const [showUploadTooltip, setShowUploadTooltip] = useState<boolean>(false);
  const [showDownloadChatTooltip, setShowDownloadChatTooltip] = useState<boolean>(false);
  const [firstHumanInputDone, setFirstHumanInputDone] = useState<boolean>(false);
  const [sessionResumed, setSessionResumed] = useState<boolean>(false);
  const [resumeAttempted, setResumeAttempted] = useState<boolean>(false);

  // NEW STATE VARIABLES FOR CASE RESUME MODAL
  const [showCaseResumeModal, setShowCaseResumeModal] = useState<boolean>(false);
  const [activeCaseInfo, setActiveCaseInfo] = useState<any>(null);
  const [caseCheckCompleted, setCaseCheckCompleted] = useState<boolean>(false);
  const [expandedMessages, setExpandedMessages] = useState<{ [key: number]: boolean }>({});
  const [showResumeUploadTooltip, setShowResumeUploadTooltip] = useState<boolean>(false);

  // Add a state to track if compact JD has already been shown
  const [compactJDShown, setCompactJDShown] = useState<boolean>(false);
  const [resumeFiles, setResumeFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resumeFileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const BACKEND_URL = "https://gln7w8w5uc.execute-api.ap-south-1.amazonaws.com/dev";
  //const BACKEND_URL = "http://localhost:5000";
  // UPDATED useEffect for checking active cases
  useEffect(() => {
    const checkForActiveCases = async (): Promise<void> => {
      if (resumeAttempted || caseCheckCompleted) return;
      setResumeAttempted(true);

      try {
        console.log("Checking for active cases...");

        const res = await fetch(`${BACKEND_URL}/check_active_cases`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) {
          console.log("Error checking active cases or no active cases found");
          setCaseCheckCompleted(true);
          setDefaultWelcomeMessage();
          return;
        }

        const data = await res.json();
        console.log("Active cases check response:", data);

        if (data.has_active_cases) {
          // Show the modal asking user what they want to do
          setActiveCaseInfo(data);
          setShowCaseResumeModal(true);
        } else {
          // No active cases, proceed normally
          setCaseCheckCompleted(true);
          setDefaultWelcomeMessage();
        }

      } catch (error) {
        console.error('Error checking active cases:', error);
        setCaseCheckCompleted(true);
        setDefaultWelcomeMessage();
      }
    };

    if (user && !resumeAttempted) {
      checkForActiveCases();
    }
  }, [user, resumeAttempted, caseCheckCompleted]);





  const pollForCurrentCase = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/check_active_cases`, {
        method: 'GET',
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        if (data.has_active_cases && data.latest_case) {
          setPendingHumanState({
            session_id: data.latest_case.session_id || '',
            case_id: data.latest_case.case_id,
            case_number: data.case_number || 1
          });
          console.log("Updated case info:", data.latest_case.case_id);
        }
      }
    } catch (error) {
      console.error('Error getting current case:', error);
    }
  };

  // Helper function for default welcome message



  const setDefaultWelcomeMessage = (): void => {
    const username = user?.name || user?.email || "";
    setMessages([
      {
        sender: 'bot',
        text: username
          ? `Hi ${username}! Please upload the JD or provide the JD file path.`
          : 'Hi there! How can I assist you today?',
      },
    ]);
  };

  // Function to handle resuming existing cases
  const handleResumeExistingCase = async (): Promise<void> => {
    setShowCaseResumeModal(false);

    try {
      console.log("User chose to resume existing case");

      const res = await fetch(`${BACKEND_URL}/resume_active_cases`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        setJdLoading(false);
        setJdProcessingActive(false);
        throw new Error('Failed to resume active cases');
      }

      const data = await res.json();
      console.log("Resume response:", data);

      if (data.session_exists) {
        console.log("Resuming active case:", data.case_id);

        // Restore session state
        setPendingHumanState({
          session_id: data.session_id,
          case_id: data.case_id,
          case_number: data.case_number
        });

        // Restore chat history
        if (data.chat_history && data.chat_history.length > 0) {
          const restoredMessages = sanitizeIncomingMessages(data.chat_history);
          setMessages((prev: Message[]) => {
            const username = user?.name || user?.email || "";
            const greeting: Message = {
              sender: 'bot',
              text: username
                ? `Welcome back ${username}!`
                : `Welcome back! Your case has been restored.`
            };

            return [greeting, ...restoredMessages];
          });

          setSessionResumed(true);
        } else {
          const username = user?.name || user?.email || "";
          setMessages([{
            sender: 'bot',
            text: username
              ? `Welcome back ${username}!`
              : `Welcome back!`
          }]);
          setSessionResumed(true);
        }

        // Restore download links
        if (data.download_links) {
          setDownloadLinks({
            compactJd: data.download_links.compactJd || undefined,
            enhancementSummary: data.download_links.enhancementSummary || undefined,
            enhancedJd: data.download_links.enhancedJd || undefined,
            resumeRankingCsv: data.download_links.resumeRankingCsv || undefined, // Add this
            resumeRankingDocx: data.download_links.resumeRankingDocx || undefined, // Add this
            questionsAnalysis: data.download_links.questionsAnalysis || undefined, // ADD THIS


          });
        }

        // Handle pending human input
        if (data.pending_human_input && data.last_message) {
          setPendingHumanPrompt(normalizeMessageText(data.last_message));
          setJdProcessingActive(false);

          if (data.workflow_state?.stage === "awaiting_human_feedback") {
            setFirstHumanInputDone(true);
          }
        } else if (data.workflow_state?.status === "completed") {
          setJdProcessingActive(false);
        }

        // Show success notification
        showNotification('Case resumed successfully!', `Case ${data.case_number} of ${data.total_cases} - ${data.case_id.substring(0, 8)}...`, '#4CAF50');

      } else {
        throw new Error('No active case could be resumed');
      }

    } catch (error) {
      console.error('Error resuming case:', error);
      alert('Failed to resume case. Starting fresh session.');
      setJdLoading(false);
      setJdProcessingActive(false);
      handleStartFreshSession();
    } finally {
      setCaseCheckCompleted(true);
    }
  };

  // Function to handle starting fresh session
  const handleStartFreshSession = async (): Promise<void> => {
    setShowCaseResumeModal(false);

    try {
      console.log("User chose to start fresh session");

      const res = await fetch(`${BACKEND_URL}/start_fresh_session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        throw new Error('Failed to start fresh session');
      }

      const data = await res.json();
      console.log("Fresh session response:", data);

      // Set fresh session state
      setPendingHumanState({
        session_id: data.session_id,
        case_id: '',
        case_number: 0
      });

      // Set default welcome message
      setDefaultWelcomeMessage();

      // Show success notification
      showNotification('Fresh session started!', `${data.cases_completed} previous case(s) archived.`, '#2196F3');

    } catch (error) {
      console.error('Error starting fresh session:', error);
      alert('Failed to start fresh session. Please refresh the page.');
    } finally {
      setCaseCheckCompleted(true);
    }
  };

  // Helper function for notifications
  const showNotification = (title: string, subtitle: string, color: string): void => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${color};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-weight: bold;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      max-width: 300px;
    `;
    notification.innerHTML = `${title}<br><small>${subtitle}</small>`;
    document.body.appendChild(notification);

    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };


  const handleResumeFolderUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const allowedExtensions = ['pdf', 'docx', 'txt'];
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      Array.from(files).forEach(file => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext && allowedExtensions.includes(ext)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        alert(
          `The following file(s) are not valid resume files and will be ignored:\n\n${invalidFiles.join(
            '\n'
          )}\n\nOnly .pdf, .docx, and .txt files are allowed.`
        );
      }

      if (validFiles.length > 0) {
        setResumeFiles(validFiles);
        alert(
          `${validFiles.length} valid resume file(s) uploaded. Click on "Process Resume" to continue.`
        );
      } else {
        setResumeFiles([]);
      }
    }
    e.target.value = '';
  };

  const handleLogout = async () => {

    const isCaseCompleted =
      pendingHumanState?.case_id &&
      downloadLinks.compactJd &&
      downloadLinks.enhancedJd &&
      downloadLinks.enhancementSummary &&
      downloadLinks.questionsAnalysis &&
      downloadLinks.resumeRankingCsv;

    const hasActiveSession =
      pendingHumanState?.session_id &&
      pendingHumanState?.case_id &&
      !isCaseCompleted ||
      jdProcessingActive;

    if (hasActiveSession) {
      const confirmLogout = window.confirm(
        'A session is in progress. Are you sure you want to log out?\n\n' +
        'Your progress will be saved and you can resume later.'
      );
      if (!confirmLogout) {
        return;
      }
    }

    try {
      const res = await fetch(`${BACKEND_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        navigate('/signin');
      } else {
        console.error('Logout failed');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedExtensions = ['pdf', 'docx', 'txt'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert("Please upload a relevant JD file with a .pdf, .docx, or .txt extension.");
        e.target.value = '';
        return;
      }

      setJdFiles(prevFiles => [...prevFiles, file]);
      alert(`File uploaded: ${file.name}, Press "Process JD" to continue.`);
    }
    e.target.value = '';
  };

  const processJD = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasFile = jdFiles.length > 0;
    const hasPath = jdPath.trim();

    if (!hasFile && !hasPath) {
      alert("Please upload a JD file or enter a valid JD file path to process.");
      setJdLoading(false);
      setJdProcessingActive(false);
      return;
    }

    if (jdProcessingActive) return;

    // Check if we have a session - if not, create one
    let sessionId = pendingHumanState?.session_id;
    if (!sessionId) {
      try {
        const res = await fetch(`${BACKEND_URL}/start_fresh_session`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error('Failed to create session');
        }

        const data = await res.json();

        // Add proper type checking and fallback
        if (!data.session_id || typeof data.session_id !== 'string') {
          throw new Error('No valid session ID received from server');
        }

        // Now TypeScript knows sessionId is definitely a string
        sessionId = data.session_id as string;

        setPendingHumanState({
          session_id: sessionId,
          case_id: '',
          case_number: 0
        });
      } catch (error) {
        console.error('Failed to initialize session:', error);
        alert("Failed to initialize session. Please refresh the page.");
        return;
      }
    }

    setJdLoading(true)
    setJdProcessingActive(true)

    try {
      if (hasPath) {
        const res = await fetch(`${BACKEND_URL}/process_jd`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ jd_path: jdPath }),
        });

        if (res.status === 400) {
          const errorData = await res.json();
          alert(errorData.detail + "\n\nPlease refresh the page.");
          setJdLoading(false);
          setJdProcessingActive(false);
          return;
        }

        const data = await res.json();
        if (!res.ok) {
          setJdLoading(false);
          setJdProcessingActive(false);
          throw new Error(data.detail || 'Failed to process JD');
        }

        handleJDProcessingResponse(data);
      } else if (hasFile) {
        const file = jdFiles[0];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${BACKEND_URL}/process_jd_file`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (res.status === 400) {
          const errorData = await res.json();
          alert(errorData.detail + "\n\nPlease refresh the page.");
          setJdLoading(false);
          setJdProcessingActive(false);
          return;
        }

        const data = await res.json();
        if (!res.ok) {
          setJdLoading(false);
          setJdProcessingActive(false);
          throw new Error(data.detail || 'Failed to process JD file');
        }

        handleJDProcessingResponse(data);
      }
    } catch (err: any) {
      console.error('Error processing JD:', err);
      alert(err.message || 'Failed to process JD');
      setJdProcessingActive(false);
    } finally {
      setJdLoading(false);
    }
  };

  const handleJDProcessingResponse = (data: any): void => {
    console.log("JD Processing Response:", data);

    // Handle case where JD is already processed
    if (data.result === "This JD is already processed.Please Upload the Resumes.") {
      setMessages((prev: Message[]) => [
        ...prev,
        { sender: 'bot', text: data.result }
      ]);

      // Set the session and case info from the response
      if (data.session_id && data.case_id) {
        setPendingHumanState({
          session_id: data.session_id,
          case_id: data.case_id,
          case_number: data.case_number || 1
        });
        console.log(`Resumed existing case: ${data.case_id}`);
      }

      setJdProcessingActive(false);
      setJdLoading(false);
      return;
    }

    // NEW: Handle background processing response
    if (data.processing && data.case_id) {
      setMessages((prev: Message[]) => [
        ...prev,
        { sender: 'bot', text: data.message }
      ]);

      setPendingHumanState({
        session_id: data.session_id,
        case_id: data.case_id,
        case_number: data.case_number || 1
      });

      // Start polling for results
      pollForJDResults(data.case_id);
      return;
    }

    // Handle session and case info
    if (data.session_id && data.case_id) {
      setPendingHumanState({
        session_id: data.session_id,
        case_id: data.case_id,
        case_number: data.case_number
      });

      console.log(`Started processing Case ${data.case_number}: ${data.case_id}`);
      if (data.filename) {
        console.log(`Processing file: ${data.filename}`);
      }
    }

    // Handle human input requirement
    if (data.human_required && data.prompt) {
      setMessages((prev: Message[]) => [
        ...prev,
        { sender: 'bot', text: data.prompt }
      ]);
      setPendingHumanPrompt(data.prompt);
      setPendingHumanState(data);
      setJdProcessingActive(false);
      setJdLoading(false);
      return;
    }

    // Check if response needs human input (legacy support)
    const needsHumanInput =
      typeof data.result === 'string' &&
      (
        data.result.toLowerCase().includes('missing') ||
        data.result.toLowerCase().includes('please provide')
      );

    if (needsHumanInput) {
      setMessages((prev: Message[]) => [
        ...prev,
        { sender: 'bot', text: data.result }
      ]);
      setPendingHumanPrompt(data.result);
      setPendingHumanState(data);
      setJdLoading(false);
      return;
    }

    // Handle immediate completion with JD generation
    if (data.jd_generation) {
      const jdGen = data.jd_generation;
      setDownloadLinks({
        compactJd: jdGen.compact_jd || undefined,
        enhancementSummary: jdGen.enhancement_summary || undefined,
        enhancedJd: jdGen.new_jd || undefined,
      });

      const successMessage = data.case_id
        ? `JD processing completed! Case ${data.case_number}: ${data.case_id.substring(0, 8)}... \nDownload links are now available below.`
        : 'JD processing completed! Download links are now available below the processing sections.';

      setMessages((prev: Message[]) => [...prev, {
        sender: 'bot',
        text: successMessage
      }]);
      setJdProcessingActive(false);
      setJdLoading(false);
      return;
    }

    // Handle result sections (legacy support)
    if (data.result_sections && Array.isArray(data.result_sections)) {
      data.result_sections.forEach((section: { header: string; content: string }) => {
        setMessages((prev: Message[]) => [
          ...prev,
          { sender: 'bot', text: `${section.header}\n${section.content}` }
        ]);
      });
      setJdProcessingActive(false);
      setJdLoading(false);
      return;
    }

    // Handle simple result (legacy support)
    if (data.result) {
      setMessages((prev: Message[]) => [...prev, { sender: 'bot', text: data.result }]);
      setJdProcessingActive(false);
      setJdLoading(false);
      return;
    }

    setJdProcessingActive(false);
    setJdLoading(false);
  };

  // Update the processResume function:
  const processResume = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check for active JD case FIRST
    if (!pendingHumanState?.case_id) {
      alert("No active case found. Please process a JD first.");
      setResumeLoading(false);
      return;
    }

    // Use resumeFiles state instead of input ref
    const hasUploadedFiles = resumeFiles.length > 0;

    // If neither path nor files are provided, alert
    if (!resumePath.trim() && !hasUploadedFiles) {
      alert("Please provide a valid folder path or upload resume files for resume processing.");
      return;
    }

    setResumeLoading(true);

    try {
      let res: Response | undefined = undefined;
      let data: any = undefined;

      if (hasUploadedFiles) {
        // File upload logic for resumes (multipart/form-data)
        const formData = new FormData();
        for (let i = 0; i < resumeFiles.length; i++) {
          formData.append('files', resumeFiles[i], resumeFiles[i].webkitRelativePath || resumeFiles[i].name);
        }
        formData.append('case_id', pendingHumanState.case_id);
        formData.append('session_id', pendingHumanState.session_id);

        res = await fetch(`${BACKEND_URL}/process_resume_folder`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        data = await res.json();

        // Clear resumeFiles after upload
        setResumeFiles([]);
      } else if (resumePath.trim()) {
        // Path-based resume processing (JSON)
        res = await fetch(`${BACKEND_URL}/process_resume_folder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ folder_path: resumePath }),
        });
        data = await res.json();
      }

      if (!res || !res.ok) {
        throw new Error((data && data.detail) || 'Failed to process resumes');
      }

      if (data && data.message) {
        setMessages((prev) => [
          ...prev,
          { sender: 'bot', text: data.message }
        ]);
      }

      if (data && data.processing && data.case_id) {
        pollForResumeResults(data.case_id);
      }
    } catch (err: any) {
      console.error('Error processing resumes:', err);
      alert(err.message || 'Failed to process resumes');
      setMessages((prev: Message[]) => [
        ...prev,
        { sender: 'bot', text: `Error processing resumes: ${err.message}` }
      ]);
    } finally {
      setResumeLoading(false);
    }
  };

  function pollForResumeResults(caseId: string) {
    setJdProcessingActive(true); // Show loading animation
    lastPollingMessageRef.current = ''; // Reset tracking

    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/cases/${caseId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!res.ok) return;

        const data = await res.json();
        const pollingData = data.polling_data;

        if (pollingData && pollingData.status === "completed") {
          clearInterval(pollInterval);
          setJdProcessingActive(false);

          // Update download links including CSV
          if (pollingData.download_links) {
            setDownloadLinks(prev => ({
              ...prev,
              resumeRankingCsv: pollingData.download_links.resumeRankingCsv || undefined,
              resumeRankingDocx: pollingData.download_links.resumeRankingDocx || undefined,
            }));
          }

          // Show completion message
          const lastMessage = normalizeMessageText(
            pollingData.last_agent_message,
            'Resume processing completed! CSV download is now available.'
          ) || 'Resume processing completed! CSV download is now available.';
          if (lastPollingMessageRef.current !== lastMessage) {
            lastPollingMessageRef.current = lastMessage;
            setMessages(prev => [
              ...prev,
              { sender: 'bot', text: lastMessage }
            ]);
          }

        } else if (pollingData && pollingData.status === "processing_resumes") {
          // Keep showing loading
          setJdProcessingActive(true);

        } else if (pollingData && pollingData.status === "agent_response_ready") {
          setJdProcessingActive(false);

          const agentResponseMessage = normalizeMessageText(pollingData.last_agent_message);
          if (agentResponseMessage && agentResponseMessage !== lastPollingMessageRef.current) {
            lastPollingMessageRef.current = agentResponseMessage;
            setMessages(prev => [
              ...prev,
              { sender: 'bot', text: agentResponseMessage }
            ]);
          }

        } else if (pollingData && pollingData.status === "error") {
          clearInterval(pollInterval);
          setJdProcessingActive(false);

          const errorMessage = 'An error occurred during resume processing. Please try again.';
          if (lastPollingMessageRef.current !== errorMessage) {
            lastPollingMessageRef.current = errorMessage;
            setMessages(prev => [
              ...prev,
              { sender: 'bot', text: errorMessage }
            ]);
          }
        }

      } catch (err) {
        console.error('Error polling for resume results:', err);
      }
    }, 3000); // Poll every 3 seconds
  }


  const lastPollingMessageRef = useRef<string>('');

  function pollForJDResults(caseId: string) {
    setJdProcessingActive(true);
    lastPollingMessageRef.current = '';

    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/cases/${caseId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        if (!res.ok) return;

        const data = await res.json();
        const pollingData = data.polling_data;

        if (pollingData && (pollingData.status === "completed" || pollingData.status === "agent_response_ready")) {
          clearInterval(pollInterval);
          setJdProcessingActive(false);

          // Handle download links for completed cases
          if (pollingData.download_links) {
            setDownloadLinks({
              compactJd: pollingData.download_links.compactJd || undefined,
              enhancementSummary: pollingData.download_links.enhancementSummary || undefined,
              enhancedJd: pollingData.download_links.enhancedJd || undefined,
              resumeRankingCsv: pollingData.download_links.resumeRankingCsv || undefined,
              resumeRankingDocx: pollingData.download_links.resumeRankingDocx || undefined,
              questionsAnalysis: pollingData.download_links.questionsAnalysis || undefined,
            });
          }

          const lastMessage = normalizeMessageText(pollingData.last_agent_message) ||
            (pollingData.status === "completed" ?
              'JD processing completed! Download links are now available below.' :
              'JD processing requires your input.');

          if (lastPollingMessageRef.current !== lastMessage) {
            lastPollingMessageRef.current = lastMessage;
            setMessages(prev => [
              ...prev,
              { sender: 'bot', text: lastMessage }
            ]);
          }

          // Only show compact JD summary ONCE per case, and NOT after APPROVE
          if (
            data.jd_results?.compact_jd &&
            pollingData.status === "completed" &&
            !compactJDShown &&
            !firstHumanInputDone // <-- Only show if not after human feedback/APPROVE
          ) {
            setMessages(prev => [
              ...prev,
              {
                sender: 'bot',
                text: `📋 **Compact JD Summary:**\n\n${data.jd_results.compact_jd}`
              }
            ]);
            setCompactJDShown(true);
          }

          // If agent response is ready (human input needed), show the case resume modal
          if (pollingData.status === "agent_response_ready") {
            setPendingHumanPrompt(lastMessage);
            setPendingHumanState({
              session_id: data.session_id,
              case_id: caseId,
              case_number: 1
            });
          }

        } else if (pollingData && pollingData.status === "processing") {
          // Keep showing loading
          setJdProcessingActive(true);

        } else if (pollingData && pollingData.status === "error") {
          clearInterval(pollInterval);
          setJdProcessingActive(false);

          const errorMessage = normalizeMessageText(
            pollingData.last_agent_message,
            'An error occurred during processing. Please try again.'
          ) || 'An error occurred during processing. Please try again.';
          if (lastPollingMessageRef.current !== errorMessage) {
            lastPollingMessageRef.current = errorMessage;
            setMessages(prev => [
              ...prev,
              { sender: 'bot', text: errorMessage }
            ]);
          }
        }

      } catch (err) {
        console.error('Error polling for JD results:', err);
      }
    }, 3000);
  }

  const handleSendMessage = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setChatLoading(true);
    setMessages((prev: Message[]) => [...prev, { sender: 'user', text: newMessage }]);
    setNewMessage('');

    const payload = {
      user_input: newMessage,
      session_id: pendingHumanState?.session_id || '',
    };

    console.log("Sending to /human_reply:", payload);
    if (pendingHumanState?.case_id) {
      console.log(`Continuing Case ${pendingHumanState.case_number}: ${pendingHumanState.case_id}`);
    }

    try {
      const res = await fetch(`${BACKEND_URL}/human_reply`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Backend error response:", errorText);
        throw new Error(`Failed with status ${res.status}`);
      }

      const data = await res.json();
      console.log("Response from /human_reply:", data);

      // NEW: Handle background processing response
      if (data.processing && data.case_id) {
        setJdProcessingActive(true); // Show loading
        pollForJDResults(data.case_id); // Start polling

        if (data.message) {
          setMessages(prev => [
            ...prev,
            { sender: 'bot', text: data.message }
          ]);
        }
        setPendingHumanPrompt(null); // Clear pending prompt
        setChatLoading(false);
        return;
      }

      // Handle session and case updates
      if (data.session_id && data.case_id) {
        setPendingHumanState((prev: PendingHumanState | null) => ({
          ...prev,
          session_id: data.session_id,
          case_id: data.case_id,
          case_number: data.case_number
        }));

        console.log(`Case ${data.case_number} updated: ${data.case_id}`);
      }

      // Handle immediate completion with JD generation
      if (data.jd_generation) {
        const jdGen = data.jd_generation;

        setDownloadLinks({
          compactJd: jdGen.compact_jd || undefined,
          enhancementSummary: jdGen.enhancement_summary || undefined,
          enhancedJd: jdGen.new_jd || undefined,
        });

        // Show feedback prompt only for the first human input
        if (!firstHumanInputDone) {
          const feedbackMessage = data.case_id
            ? `JD generated for Case ${data.case_number}: ${data.case_id.substring(0, 8)}...\n\nPlease check the JD and provide your feedback`
            : 'Please check the JD and provide your feedback';

          setMessages((prev: Message[]) => [...prev, {
            sender: 'bot',
            text: feedbackMessage
          }]);
          setFirstHumanInputDone(true);
        }
      }

      // Handle result sections
      if (data.result_sections && Array.isArray(data.result_sections)) {
        data.result_sections.forEach((section: { header: string; content: string }) => {
          setMessages((prev: Message[]) => [
            ...prev,
            { sender: 'bot', text: `${section.header}\n${section.content}` }
          ]);
        });
      } else if (data.result) {
        setMessages((prev: Message[]) => [...prev, { sender: 'bot', text: data.result }]);
      }

      // Handle human input requirement
      if (data.human_required && data.result) {
        setMessages((prev: Message[]) => [
          ...prev,
          { sender: 'bot', text: data.result }
        ]);
        setPendingHumanPrompt(data.result);
      } else {
        setPendingHumanPrompt(null);

        if (data.case_id) {
          console.log(`Case ${data.case_number} completed: ${data.case_id}`);
        }
      }

    } catch (err: any) {
      console.error("Failed to send human input:", err);
      alert(err.message || 'Failed to send human input');
    } finally {
      setChatLoading(false);
    }
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--background-color)' }}>

      {/* Header with Logo */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
        <img src={logo} alt="TechWise Logo" style={{ height: 40 }} />
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={userIcon}
            alt="User"
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              cursor: 'pointer',
              border: '1px solid #ccc',
              background: '#fff'
            }}
            onClick={() => setShowUserMenu(prev => !prev)}
          />
          {showUserMenu && (
            <div
              style={{
                position: 'absolute',
                top: 40,
                right: 0,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                minWidth: 120,
                zIndex: 100,
              }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setShowUserMenu(false);
                  setShowProfileModal(true);
                }}
              >
                Profile
              </button>
              <button
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#C54040'
                }}
                onClick={() => {
                  setShowUserMenu(false);
                  handleLogout();
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>

        {showProfileModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={() => setShowProfileModal(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: 24,
                minWidth: 300,
                position: 'relative',
              }}
              onClick={e => e.stopPropagation()}
            >
              <h2 style={{ marginBottom: 16, textAlign: 'center' }}>Profile</h2>
              <div style={{ marginBottom: 8 }}>
                <strong>Name:</strong> {user?.name || 'N/A'}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>Email:</strong> {user?.email || 'N/A'}
              </div>
              <button
                style={{
                  marginTop: 16,
                  padding: '8px 16px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#007bff',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                }}
                onClick={() => setShowProfileModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Loading Animation */}
      {(jdLoading || jdProcessingActive) && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.5)',
          zIndex: 9999,
        }}>
          <div className="loader"></div>
        </div>
      )}

      {/* Case Resume Modal */}
      {showCaseResumeModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              padding: 32,
              minWidth: 400,
              maxWidth: 500,
              position: 'relative',
            }}
          >
            <h2 style={{
              marginBottom: 16,
              textAlign: 'center',
              color: 'var(--primary-color)',
              fontSize: '20px'
            }}>
              Active Case Found
            </h2>

            {activeCaseInfo && (
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  background: '#f8f9fa',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16
                }}>
                  <p><strong>Cases:</strong> {activeCaseInfo.total_cases}</p>
                  <p><strong>File:</strong> {activeCaseInfo.latest_case.file_info}</p>
                  <p><strong>Status:</strong> {activeCaseInfo.latest_case.workflow_stage}</p>
                  <p><strong>Last Update:</strong> {new Date(activeCaseInfo.latest_case.updated_at).toLocaleString()}</p>
                  {activeCaseInfo.latest_case.requires_input && (
                    <p style={{ color: '#C54040' }}>
                      <strong>⚠️ Requires your input</strong>
                    </p>
                  )}
                </div>

                <p style={{ textAlign: 'center', marginBottom: 20, fontSize: '14px', color: '#666' }}>
                  Would you like to continue with your existing case or start fresh?
                </p>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center'
            }}>
              <button
                style={{
                  padding: '12px 24px',
                  borderRadius: 6,
                  border: '2px solid #4CAF50',
                  background: '#4CAF50',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  minWidth: '120px',
                }}
                onClick={handleResumeExistingCase}
              >
                Continue Case
              </button>
              <button
                style={{
                  padding: '12px 24px',
                  borderRadius: 6,
                  border: '2px solid #2196F3',
                  background: '#2196F3',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  minWidth: '120px',
                }}
                onClick={handleStartFreshSession}
              >
                Start Fresh
              </button>
            </div>

            <p style={{
              textAlign: 'center',
              marginTop: 16,
              fontSize: '12px',
              color: '#666'
            }}>
              Starting fresh will archive your current case(s)
            </p>
          </div>
        </div>
      )}

      {/* Main Content Area: Split left/right */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left: JD & Resume Processing */}
        <div style={{
          flex: 1,
          padding: '200px 20px 20px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          background: 'transparent',
          minWidth: 0,
          maxWidth: '50vw',
        }}>
          {/* JD Processing Section */}
          <div style={{ background: '#fff', padding: 10, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: 12, fontSize: '18px', color: 'var(--primary-color)' }}>Job Description Processing</h3>
            <form onSubmit={processJD} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                <button
                  type="button"
                  onClick={handleUploadClick}
                  onMouseEnter={() => setShowUploadTooltip(true)}
                  onMouseLeave={() => setShowUploadTooltip(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                    position: 'relative',
                  }}
                  aria-label="Upload JD"
                >
                  <UploadIcon />
                  {showUploadTooltip && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-32px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#333',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      Upload JD
                    </div>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <input
                  type="text"
                  value={jdPath}
                  onChange={e => setJdPath(e.target.value)}
                  placeholder="Enter JD file path..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <button
                  type="submit"
                  onMouseEnter={() => setHoverJD(true)}
                  onMouseLeave={() => setHoverJD(false)}
                  disabled={jdLoading || jdProcessingActive}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: 'none',
                    background: (jdLoading || jdProcessingActive) ? '#ccc' : (hoverJD ? '#000' : '#fff'),
                    color: (jdLoading || jdProcessingActive) ? '#888' : (hoverJD ? '#fff' : '#C54040'),
                    fontWeight: 'bold',
                    fontSize: '14px',
                    cursor: (jdLoading || jdProcessingActive) ? 'not-allowed' : 'pointer',
                    width: '150px',
                    textAlign: 'center',
                    opacity: (jdLoading || jdProcessingActive) ? 0.6 : 1,
                  }}
                >
                  {(jdLoading || jdProcessingActive) ? 'Processing...' : 'Process JD'}
                </button>
              </div>
            </form>
          </div>

          {/* Resume Processing Section */}
          <div style={{ background: '#fff', padding: 10, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: 12, fontSize: '18px', color: 'var(--primary-color)' }}>Resume Processing</h3>
            <form onSubmit={processResume} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => resumeFileInputRef.current?.click()}
                  onMouseEnter={() => setShowResumeUploadTooltip(true)}
                  onMouseLeave={() => setShowResumeUploadTooltip(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                    position: 'relative',
                  }}
                  aria-label="Upload Resume "
                >
                  <UploadIcon />
                  {showResumeUploadTooltip && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-32px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#333',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      Upload Resumes
                    </div>
                  )}
                </button>
                <input
                  type="file"
                  ref={resumeFileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  onChange={handleResumeFolderUpload}
                  // @ts-ignore
                  webkitdirectory
                />
                <input
                  type="text"
                  value={resumePath}
                  onChange={e => setResumePath(e.target.value)}
                  placeholder="Enter Resume folder path..."
                  style={{
                    width: '96%',
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <button
                  type="submit"
                  onMouseEnter={() => setHoverResume(true)}
                  onMouseLeave={() => setHoverResume(false)}
                  disabled={resumeLoading || jdProcessingActive}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: 'none',
                    background: (resumeLoading || jdProcessingActive) ? '#ccc' : (hoverResume ? '#000' : '#fff'),
                    color: (resumeLoading || jdProcessingActive) ? '#888' : (hoverResume ? '#fff' : '#C54040'),
                    fontWeight: 'bold',
                    fontSize: '14px',
                    cursor: (resumeLoading || jdProcessingActive) ? 'not-allowed' : 'pointer',
                    width: '150px',
                    textAlign: 'center',
                    opacity: (resumeLoading || jdProcessingActive) ? 0.6 : 1,
                  }}
                >
                  {(resumeLoading || jdProcessingActive) ? 'Processing...' : 'Process Resume'}
                </button>
              </div>
            </form>
          </div>

          {/* Download Links Section */}
          {/* {(downloadLinks.compactJd || downloadLinks.enhancementSummary || downloadLinks.enhancedJd) && (
            <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
              <h4 style={{ marginBottom: 12, fontSize: '16px', color: 'var(--primary-color)' }}>Download Processed JD</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {downloadLinks.compactJd && (
                  <button
                    onClick={() => downloadTextFile('Compact_JD.txt', downloadLinks.compactJd!)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    JD Extract
                  </button>
                )}
                {downloadLinks.enhancementSummary && (
                  <button
                    onClick={() => downloadTextFile('Enhancement_Summary.txt', downloadLinks.enhancementSummary!)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    JD Process Summary
                  </button>
                )}
                {downloadLinks.enhancedJd && (
                  <button
                    onClick={() => downloadTextFile('Enhanced_JD.txt', downloadLinks.enhancedJd!)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Enhanced JD
                  </button>
                )}
              </div>
            </div>
          )} */}
          {(downloadLinks.compactJd || downloadLinks.enhancementSummary || downloadLinks.enhancedJd || downloadLinks.resumeRankingCsv || downloadLinks.questionsAnalysis) && (
            <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
              <h4 style={{ marginBottom: 12, fontSize: '16px', color: 'var(--primary-color)' }}>Download Processed Outputs</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {downloadLinks.compactJd && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.compactJd}`, 'Compact_JD.docx')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bffff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    JD Extract (DOCX)
                  </button>
                )}
                {downloadLinks.enhancementSummary && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.enhancementSummary}`, 'Enhancement_Summary.docx')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    JD Process Summary (DOCX)
                  </button>
                )}
                {downloadLinks.enhancedJd && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.enhancedJd}`, 'Enhanced_JD.docx')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Enhanced JD (DOCX)
                  </button>
                )}
                {downloadLinks.questionsAnalysis && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.questionsAnalysis}`, 'Questions_Analysis.txt')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bffff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Questions & Analysis (TXT)
                  </button>
                )}
                {downloadLinks.resumeRankingCsv && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.resumeRankingCsv}`, 'Resume_Ranking_Output.csv')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  >
                    Resume Scores (CSV)
                  </button>
                )}
                {downloadLinks.resumeRankingDocx && (
                  <button
                    onClick={() => downloadDocxFile(`${BACKEND_URL}${downloadLinks.resumeRankingDocx}`, 'Resume_Ranking_Output.docx')}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 4,
                      border: '1px solid #007bff',
                      background: '#fff',
                      color: '#007bff',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  >
                    Resume Analysis (DOCX)
                  </button>
                )}
              </div>
            </div>
          )}


        </div>

        {/* Right: Chat Section */}
        <div style={{
          flex: 1,
          padding: '32px 40px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          minWidth: 0,
          maxWidth: '50vw',
        }}>
          <div style={{
            flex: 1,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative',
          }}>
            <h3 style={{ marginBottom: 16, color: 'var(--primary-color)', textAlign: 'center' }}>Recruiter Interaction</h3>
            {chatLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                zIndex: 1000,
              }}>
                <div className="loader"></div>
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
              {messages.map((message, index) => {
                const messageText = normalizeMessageText(message.text);
                // Download links for NEW JD, COMPACT VERSION, ENHANCEMENT SUMMARY
                if (
                  message.sender === 'bot' &&
                  (
                    messageText.startsWith('NEW JD:') ||
                    messageText.startsWith('COMPACT VERSION:') ||
                    messageText.startsWith('ENHANCEMENT SUMMARY:')
                  )
                ) {
                  let header = '';
                  let content = '';
                  if (messageText.startsWith('NEW JD:')) {
                    header = 'Enhanced JD';
                    content = messageText.replace(/^NEW JD:\s*/i, '');
                  } else if (messageText.startsWith('COMPACT VERSION:')) {
                    header = 'COMPACT VERSION';
                    content = messageText.replace(/^COMPACT VERSION:\s*/i, '');
                  } else if (messageText.startsWith('ENHANCEMENT SUMMARY:')) {
                    header = 'ENHANCEMENT SUMMARY';
                    content = messageText.replace(/^ENHANCEMENT SUMMARY:\s*/i, '');
                  }
                  return (
                    <div key={index} style={{ marginBottom: 8 }}>
                      <a
                        href="#"
                        style={{
                          display: 'inline-block',
                          padding: '8px 12px',
                          borderRadius: 4,
                          background: '#e6f7ff',
                          color: '#007bff',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                        onClick={e => {
                          e.preventDefault();
                          downloadTextFile(`${header.replace(/ /g, '_')}.txt`, content);
                        }}
                      >
                        Download {header}
                      </a>
                    </div>
                  );
                }

                // Skip ANALYSIS and SUGGESTIONS
                if (
                  message.sender === 'bot' &&
                  (
                    messageText.startsWith('ANALYSIS:') ||
                    messageText.startsWith('SUGGESTIONS:')
                  )
                ) {
                  return null;
                }

                // User messages
                if (message.sender === 'user') {
                  return (
                    <div
                      key={index}
                      style={{
                        textAlign: 'right',
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '8px 12px',
                          borderRadius: 4,
                          background: '#007bff',
                          color: '#fff',
                          maxWidth: '80%',
                          wordWrap: 'break-word',
                        }}
                      >
                        {messageText}
                      </span>
                    </div>
                  );
                }

                // Bot messages with expand/collapse functionality
                if (message.sender === 'bot') {
                  const isLongMessage = messageText.length > 300;
                  const isExpanded = expandedMessages[index] || false;

                  return (
                    <div
                      key={index}
                      style={{
                        textAlign: 'left',
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '8px 12px',
                          borderRadius: 4,
                          background: '#f1f1f1',
                          color: '#000',
                          maxWidth: '80%',
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {isLongMessage && !isExpanded
                          ? messageText.substring(0, 300) + "..."
                          : messageText
                        }
                        {isLongMessage && (
                          <button
                            onClick={() => {
                              setExpandedMessages(prev => ({
                                ...prev,
                                [index]: !prev[index]
                              }));
                            }}
                            style={{
                              display: 'block',
                              marginTop: 8,
                              padding: '4px 8px',
                              fontSize: '12px',
                              border: '1px solid #007bff',
                              background: 'none',
                              color: '#007bff',
                              cursor: 'pointer',
                              borderRadius: 4,
                            }}
                          >
                            {isExpanded ? 'Show Less' : 'Show More'}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                }

                return null;
              })}
            </div>
            <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Provide your input here..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  fontSize: 14,
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '8px 12px',
                  borderRadius: 4,
                  border: 'none',
                  background: '#000000ff',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Send
              </button>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  type="button"
                  onMouseEnter={() => setShowDownloadChatTooltip(true)}
                  onMouseLeave={() => setShowDownloadChatTooltip(false)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 4,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={downloadIcon} alt="Download Chat" style={{ width: 20, height: 20 }} />
                </button>
                {showDownloadChatTooltip && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-32px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#333',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: 4,
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    Download Chat
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainApp;
